// libraries
import React, { useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, IconButton, TableRow, TableCell, Tooltip, Badge } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import DeleteIcon from '@mui/icons-material/Delete'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'

// components
import BasicDataGrid from 'components/generic/datagrid'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

import { PAYMENT_METHODS_VALUES, ENTITY_IN_LOCK_PERIOD_MESSAGE } from 'utils/constants'
import { formatNumber, dispatchSnackbar } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

// queries & mutations
import { useDeleteInvoiceBulkPaymentMutation } from 'services/invoicePayments'
import { InvoicePaymentResponseDtoSchema } from 'gen/rtk-openapi'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.Mui-selected': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '&:not(.Mui-selected)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
}))

interface InvoicePaymentTableV2Props {
  invoiceId?: string
  paymentInfo?: InvoicePaymentResponseDtoSchema[]
  loading: boolean
  pricePrepend: string
  handleOpenBulkPayment: (id: string) => void
}
interface Payment {
  id: string
  paymentDate: string
  paymentType: string
  checkNumber: string
  amountPaid: number
  adjustments: number
  notes: string
  isBulkPayment: boolean
}

const InvoicePaymentTableV2: React.FC<InvoicePaymentTableV2Props> = ({
  invoiceId,
  paymentInfo,
  loading = false,
  pricePrepend = '$',
  handleOpenBulkPayment,
}) => {
  const classes = useStyles()
  const apiRef = useGridApiRef()

  const dispatch = useDispatch()

  const [showVoidPaymentModal, setShowVoidPaymentModal] = useState(false)
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState<Payment | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])

  const [deleteInvoicePayment, { isLoading: isDeletingPayment }] = useDeleteInvoiceBulkPaymentMutation()

  // Feature flags
  const { supportVoidInvoice, enableLockAccountingV2 } = useSelector(
    state => (state as any).featureFlags?.variables || {},
  )

  const { settings } = useSelector(state => (state as any).settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  useEffect(
    function mapInvoicePaymentSplitsToPaymentArray() {
      if (!paymentInfo) return

      const mappedPayments = paymentInfo.map(paymentSplit => {
        return {
          id: paymentSplit.id,
          paymentDate: paymentSplit.date,
          paymentType: paymentSplit.method,
          checkNumber: paymentSplit.checkNumber,
          amountPaid: paymentSplit.amount,
          adjustments: paymentSplit.adjustments,
          notes: paymentSplit.notes,
          isBulkPayment: paymentSplit.isBulkPayment,
        }
      })

      setPayments(mappedPayments)
    },
    [paymentInfo],
  )

  const handleVoidInvoicePayment = async () => {
    await deleteInvoicePayment({ invoiceId, paymentId: selectedPaymentInfo?.id })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Invoice Payment Voided')
        setShowVoidPaymentModal(false)
        setSelectedPaymentInfo(null)
      })
      .catch(error => {
        const errorMessage = error?.data?.error || 'Please ensure all payments are voided prior to voiding the invoice.'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const paymentColumns = useMemo(
    () => [
      {
        field: 'paymentDate',
        headerName: 'Payment Date',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueFormatter: params => {
          const { value } = params
          return dateFormatter(new Date(`${value}T00:00:00`))
        },
        sortable: false,
      },
      {
        field: 'paymentType',
        headerName: 'Payment Method',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueFormatter: params => {
          const { value } = params
          return PAYMENT_METHODS_VALUES[value?.toUpperCase()] || ''
        },
        sortable: false,
      },
      {
        field: 'checkNumber',
        headerName: 'Check #',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'amountPaid',
        headerName: 'Payment Amount',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const amountPaid = Number(params.row.amountPaid)
          return isNaN(amountPaid) ? 0 : amountPaid
        },
        valueFormatter: params => {
          return `${pricePrepend}${formatNumber(params.value.toFixed(2))}`
        },
        sortable: false,
      },
      {
        field: 'adjustments',
        headerName: 'Adjustments',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const adjustments = Number(params.row.adjustments)
          return isNaN(adjustments) ? 0 : adjustments
        },
        valueFormatter: params => {
          return `${pricePrepend}${formatNumber(params.value.toFixed(2))}`
        },
        sortable: false,
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 3,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => {
          const { row } = params
          const { paymentDate } = row || {}

          let isInLockedAccountingPeriodV1

          if (enableLockAccountingV2) {
            isInLockedAccountingPeriodV1 = false // if lock accounting V2 is turned on, we do not need to disable any fields
          } else if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) {
            isInLockedAccountingPeriodV1 = false
          } else {
            isInLockedAccountingPeriodV1 =
              new Date(`${paymentDate}T00:00:00`).getTime() <
              new Date(lockAccountingPeriod.lockAccountingDate).getTime()
          }

          return row.id === 'total' ? null : (
            <Tooltip title={isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''}>
              <div>
                {row.isBulkPayment ? (
                  <Tooltip title="Open bulk payment" placement="left">
                    <IconButton
                      size="small"
                      aria-label="open-bulk-payment"
                      data-testid={`open-bulk-payment-${row.id}`}
                      data-payment-type="bulk"
                      onClick={() => handleOpenBulkPayment(row.id)}
                      disabled={isInLockedAccountingPeriodV1}
                    >
                      <Badge color="primary">
                        <OpenInNewIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                ) : (
                  <IconButton
                    color="error"
                    size="small"
                    sx={{
                      fill: 'red',
                    }}
                    aria-label="delete"
                    data-testid={`delete-payment-${row.id}`}
                    data-payment-type="default-payment"
                    onClick={() => {
                      setSelectedPaymentInfo(row)
                      setShowVoidPaymentModal(true)
                    }}
                    disabled={supportVoidInvoice ? isInLockedAccountingPeriodV1 : true}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            </Tooltip>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ],
    [payments, pricePrepend, handleOpenBulkPayment, lockAccountingPeriod, enableLockAccountingV2],
  )

  return (
    <Box>
      <AlertModal
        open={showVoidPaymentModal}
        modalType="delete"
        title="Void This Payment"
        question="Are you sure you want to void this payment?"
        description={
          <>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Date:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {dateFormatter(new Date(`${selectedPaymentInfo?.paymentDate}T00:00:00`))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Method:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {selectedPaymentInfo?.paymentType
                  ? PAYMENT_METHODS_VALUES[selectedPaymentInfo?.paymentType?.toUpperCase()]
                  : ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Check Number:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {selectedPaymentInfo?.checkNumber || ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Amount:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {pricePrepend}
                {formatNumber((Number(selectedPaymentInfo?.amountPaid) || 0).toFixed(2))}
              </TableCell>
            </TableRow>
          </>
        }
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidPaymentModal(false)
          setSelectedPaymentInfo(null)
        }}
        onSubmit={handleVoidInvoicePayment}
      />
      <BasicDataGrid
        className={classes.root}
        density="compact"
        height="auto"
        apiRef={apiRef}
        loading={loading || isDeletingPayment}
        rows={payments}
        columns={paymentColumns}
        disableColumnMenu
        hideFooter
      />
    </Box>
  )
}

export default InvoicePaymentTableV2
